import { useContext, useMemo } from 'react';
import { ParentContext } from '../ParentContext';
import { UiItemContext } from '../UiItemContext';
import { useAppUserAccess } from './useAppUserAccess';
import { useDataModifications } from './useDataModifications';

export const showTypes_longPress = {
  appData: 'appData',
  appSettings: 'appSettings',
  appUser: 'appUser',
  auth: 'auth',
  connections: 'connections',
  data: 'data',
  interactions: 'interactions',
  itemAction: 'itemAction',
  itemInteractions: 'itemInteractions',
  myProfilePage: 'myProfilePage',
  openDatabase: 'openDatabase',
  pageSettings: 'pageSettings',
  qrCodeAppUser: 'qrCodeAppUser',
  qrCodeItem: 'qrCodeItem',
  scanAppUser: 'scanAppUser',
  scanItem: 'scanItem',
  selectImage: 'selectImage',
  setAsBanner: 'setAsBanner',
  setAsManifest: 'setAsManifest',
  viewItem: 'viewItem',
}

export const useItemSquares = () => {

  const appUserAccess = useAppUserAccess()
  const { appUserSession } = appUserAccess ?? {}
  const { appUserSessionKey } = appUserSession ?? {}

  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { currentSettings_state } = states

  const { aps_global, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appGamification, appNetworking, appSettings } = aps_global ?? {};
  const { allowGamification, allowedGamificationPageItems } = appGamification ?? {}
  const { allowNetworking } = appNetworking ?? {}

  // uiItemContext
  const uiItemContext = useContext(UiItemContext);
  const { item_state } = uiItemContext ?? {}
  const { viewItem_key: viKey } = item_state ?? {}

  const { dmOpts } = useDataModifications({ isLongPress: true })
  const { list, solo, subList } = dmOpts ?? {}

  const allowGamificationItem = allowGamification && _.includes(allowedGamificationPageItems, viKey)

  const {
    appUserCollection,
    allowCloning,
  } = aps_appUserSettings ?? {};


  let _allowCloning = false
  let _allowAppUserMods = false

  if (viKey === appUserCollection) {
    _allowCloning = allowCloning
    _allowAppUserMods = true
  }


  const _dmo_solo = useMemo(() => {
    return _.fromPairs(
      _.sortBy(_.toPairs(solo), ([key]) => key)
    );
  }, [solo]);

  const _dmo_list = useMemo(() => {
    return _.fromPairs(
      _.sortBy(_.toPairs(list), ([key]) => key)
    );
  }, [list])

  const _dmo_subList = useMemo(() => {
    return _.fromPairs(
      _.sortBy(_.toPairs(subList), ([key]) => key)
    );
  }, [subList])

  const actions_item = useMemo(() => {
    const items = {}
    if (allowGamification && _.includes(allowedGamificationPageItems, viKey)) {
      items.interactions = { showType: showTypes_longPress.itemInteractions }
    }
    return items
  }, [])

  const actions_image = useMemo(() => {
    const items = {}
    items.setAsBanner = { showType: showTypes_longPress.setAsBanner }
    items.setAsManifest = { showType: showTypes_longPress.setAsManifest }
    return items
  }, [])


  const actions_superAdminDeveloper = useMemo(() => {
    const items = {
      openDatabase: { showType: showTypes_longPress.openDatabase, icon: 'external alternate', iconColor: 'orange' },
    }
    return items
  }, [])

  const actions_admin = useMemo(() => {
    const items = {
      data: { showType: showTypes_longPress.data },
      viewItem: { showType: showTypes_longPress.viewItem },
    }
    if (_allowAppUserMods) {
      items.authManager = { showType: showTypes_longPress.auth }
    }
    return items
  }, [])

  const actions_item_scans = useMemo(() => {
    const items = {}
    if (allowGamification) {
      items.showQrCode = { showType: showTypes_longPress.qrCodeItem, icon: 'qrcode' }
      items.scanItem = { showType: showTypes_longPress.scanItem, icon: 'print' }
    }
    return items
  }, [])

  const actions_settings = {
    appSettings: { showType: showTypes_longPress.appSettings },
    pageSettings: { showType: showTypes_longPress.pageSettings, icon: 'setting' }
  }

  const actions_pageSettings = {
    pageSettings: { showType: showTypes_longPress.pageSettings, icon: 'setting' }
  }

  const actions_app = {
    appData: { showType: showTypes_longPress.appData, icon: 'data' },
  }


  const actions_appUser = useMemo(() => {
    const items = {}

    if (appUserSessionKey) {
      items.myProfilePage = { showType: showTypes_longPress.myProfilePage, icon: 'file outline' }
    }

    items.profileOptions = { showType: showTypes_longPress.appUser, icon: 'options' }

    if (allowGamification) {
      items.interactions = { showType: showTypes_longPress.interactions }
    }
    if (allowNetworking) {
      items.connections = { showType: showTypes_longPress.connections, icon: 'handshake outline' }
    }
    return items
  }, [])

  const actions_appUser_scans = useMemo(() => {
    const items = {}
    if (allowGamificationItem || allowNetworking) {
      items.showQrCode = { showType: showTypes_longPress.qrCodeItem, icon: 'qrcode' }
    }
    if (allowGamification) {
      items.scanItem = { showType: showTypes_longPress.scanItem, icon: 'print' }
    }
    if (allowNetworking) {
      items.scanAppUser = { showType: showTypes_longPress.scanAppUser, icon: 'print' }
    }
    return items
  }, [])

  const actions_banner = {
    selectImage: { showType: showTypes_longPress.selectImage },
  }

  return {
    _dmo_list,
    _dmo_solo,
    _dmo_subList,
    actions_admin,
    actions_superAdminDeveloper,
    actions_appUser_scans,
    actions_appUser,
    actions_banner,
    actions_image,
    actions_item_scans,
    actions_item,
    actions_pageSettings,
    actions_settings,
    actions_app,
  }
}
